<template>
  <div class="tabs-item" ref="wrapper">
    <div class="toolbar-row">
      <el-button type="primary" size="small" @click="exportExcel">导出</el-button>
      <el-button type="primary" size="small" @click="openProgressPage">缺货回复完成进度表</el-button>
      <el-button type="primary" size="small" @click="tableVisible = true">表格设置</el-button>
    </div>

    <el-table
      ref="table"
      border
      stripe
      :data="tableData"
      :max-height="maxHeight"
      v-loading="tableData === null"
      v-if="tableReload"
      @sort-change="handleSortChange"
      :header-cell-style="{ backgroundColor: '#FAFAFA' }"
    >
      <el-table-column
        v-for="item in columnData"
        :key="item.prop"
        :label="item.label"
        :prop="item.prop"
        :fixed="item.fixed"
        :min-width="item.width"
        :sortable="item.sortable ? 'custom' : false"
        show-overflow-tooltip
        :align="item.label === '包件编码' || item.label === '包件名称' ? 'left' : 'right'"
      >
        <template slot="header">
          <template v-if="item.label === '缺货记录天数'">
            <span>缺货记录天数</span>
            <el-tooltip effect="dark" placement="top">
              <span class="el-icon-warning-outline"></span>
              <div slot="content">最近180天的缺货记录天数</div>
            </el-tooltip>
          </template>
          <template v-else-if="item.label === '计划必回复缺货量'">
            <span>计划必回复缺货量</span>
            <el-tooltip effect="dark" placement="top">
              <span class="el-icon-warning-outline"></span>
              <div slot="content">必回复明细：VSO缺口、特急缺口、优先级高缺口、未来30天内超期及已超期缺口。</div>
            </el-tooltip>
          </template>
          <template v-else-if="item.label === '已开单消耗量'">
            <span>已开单消耗量</span>
            <el-tooltip effect="dark" placement="top">
              <span class="el-icon-warning-outline"></span>
              <div slot="content">统计当前包件在本周缺货回复日到现在已经被开单消耗的包件量。</div>
            </el-tooltip>
          </template>
          <template v-else>
            <span>{{ item.label }}</span>
          </template>
        </template>

        <template slot-scope="scope">
          <template v-if="scope.column.label === '包件编码'">
            <remark :content="scope.row.packCode"></remark>
          </template>

          <template v-else-if="scope.column.label === '包件名称'">
            <remark :content="scope.row.packName"></remark>
          </template>

          <template v-else-if="scope.column.label === '缺货记录天数'">
            <el-link :underline="false" type="success" @click="handleDialogShow('record', scope.row)">{{ scope.row.shortageDays }}</el-link>
          </template>

          <template v-else-if="scope.column.label === '缺货影响订单数'">
            <el-link :underline="false" type="success" @click="handleTabsChange(scope.row, 'orders')">{{ scope.row.shortageOrderNum }}</el-link>
          </template>

          <template v-else-if="scope.column.label === '缺货影响产品数'">
            <el-link :underline="false" type="success" @click="handleTabsChange(scope.row, 'products')">{{ scope.row.shortageProductNum }}</el-link>
          </template>

          <template v-else-if="scope.column.label === '计划必回复缺货量'">
            <el-popover trigger="hover" placement="top" width="660">
              <el-table
                :data="[scope.row]"
                border
                size="mini"
                :header-cell-style="{ backgroundColor: '#F4F8FB', textAlign: 'center' }"
                :cell-style="{ textAlign: 'center' }"
              >
                <el-table-column label="入仓缺口" min-width="100">
                  <template slot-scope="{ row }">{{ getWarehouseGap(row) }}</template>
                </el-table-column>
                <el-table-column prop="vsoNum" label="VSO缺口" min-width="100" />
                <el-table-column prop="urgentQuantity" label="特急缺口" min-width="100" />
                <el-table-column prop="level1Quantity" label="优先级高缺口" min-width="120" />
                <el-table-column prop="aftTenQuantity" label="未来10天内超期及已超期缺口" min-width="180" />
              </el-table>
              <el-link slot="reference" :underline="false" type="success">{{ scope.row[item.prop] }}</el-link>
            </el-popover>
          </template>

          <template v-else-if="scope.column.label === '预计出货量'">
            <el-popover trigger="hover" placement="top" width="640" @show="loadExpectShipDaily(scope.row)">
              <div v-if="expectShipDailyMap[scope.row.packCode]" class="daily-table">
                <div
                  v-for="week in getDailyWeeks(expectShipDailyMap[scope.row.packCode])"
                  :key="week.weekLabel"
                  class="daily-week-block"
                >
                  <div class="daily-week-title">{{ week.weekLabel }}</div>
                  <div class="daily-row daily-head">
                    <span v-for="day in week.days" :key="`${week.weekLabel}-${day.dateCode}-label`">{{ day.dateLabel }}</span>
                  </div>
                  <div class="daily-row">
                    <span v-for="day in week.days" :key="`${week.weekLabel}-${day.dateCode}-value`">{{ day.quantity }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="daily-loading">加载中...</div>
              <el-link slot="reference" :underline="false" type="success">{{ scope.row.expectShipNum || scope.row[item.prop] }}</el-link>
            </el-popover>
          </template>

          <template v-else-if="scope.column.label === '已开单消耗量'">
            <el-link :underline="false" type="success" @click="openConsumedDialog(scope.row)">{{ scope.row.orderedConsumeNum || scope.row[item.prop] }}</el-link>
          </template>

          <template
            v-else-if="
              (scope.column.label.includes('下线') && scope.column.label !== '其他下线时间' && scope.column.label !== '预计下线量') ||
              scope.column.label === '欠料' ||
              scope.column.label === '异常备注'
            "
          >
            <span v-if="!scope.row.flag">{{ scope.row[item.prop] }}</span>
            <el-input
              v-else
              autosize
              type="textarea"
              v-model="scope.row[scope.column.property]"
              size="mini"
              @input="input($event, scope)"
            />
          </template>

          <template v-else>
            {{ scope.row[item.prop] }}
          </template>
        </template>
      </el-table-column>

      <el-table-column label="操作" fixed="right" align="center" width="100">
        <template slot-scope="scope">
          <el-button type="text" @click="updateAdd(scope)" :disabled="!columnJurisdiction">
            {{ !scope.row.flag ? '编辑' : '保存' }}
          </el-button>
          <el-button type="text" @click="cancel(scope)" v-if="scope.row.flag">取消</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="footer-row">
      <div class="refresh-time">
        <span>{{ timeText.updateContent ? '数据刷新时间：' : '' }}</span>
        <span>{{ timeText.latestUpdateTime ? timeText.latestUpdateTime : '' }}</span>
      </div>
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="paginationOpt.pageNum"
        :page-sizes="paginationOpt.pageSizes"
        :page-size="paginationOpt.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="paginationOpt.total"
      ></el-pagination>
    </div>

    <v-dialog v-model="dialogVisibleRecord" :config="{ showButton: false, width: '70%', title: '查看' }">
      <div slot="content">
        <outofstock-records :data="rowData" v-if="dialogVisibleRecord"></outofstock-records>
      </div>
    </v-dialog>

    <v-dialog v-model="dialogVisibleReply" :config="{ showButton: false, width: '70%', title: '查看' }">
      <div slot="content">
        <reply :data="rowData" v-if="dialogVisibleReply"></reply>
      </div>
    </v-dialog>

    <el-dialog :visible.sync="consumedDialogVisible" title="已开单消耗量" width="80%" :close-on-click-modal="false">
      <div class="consumed-header">本周回复时间：{{ consumedDialogRow.packCommitDate || '-' }}</div>
      <el-table
        :data="consumedDialogData"
        border
        height="360"
        v-loading="consumedDialogLoading"
        :header-cell-style="{ backgroundColor: '#F4F8FB', textAlign: 'center' }"
        :cell-style="{ textAlign: 'center' }"
      >
        <el-table-column prop="platformOrderNo" label="平台订单号" min-width="150" show-overflow-tooltip />
        <el-table-column prop="orderNo" label="电商单号" min-width="150" show-overflow-tooltip />
        <el-table-column prop="purchaseOrderNo" label="工厂/采购订单号" min-width="150" show-overflow-tooltip />
        <el-table-column prop="payTime" label="付款日期" min-width="140" />
        <el-table-column prop="auditTime" label="审核日期" min-width="140" />
        <el-table-column prop="latestShippingTime" label="订单最晚发货时间" min-width="160" />
        <el-table-column prop="deliveryCycle" label="交货周期（天）" min-width="120" />
        <el-table-column prop="lastDays" label="剩余发货时效（天）" min-width="140" />
        <el-table-column prop="orderSource" label="订单来源/店铺简称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="productCode" label="产品编码" min-width="120" />
        <el-table-column prop="soTime" label="开单时间" min-width="140" />
      </el-table>
    </el-dialog>

    <custom-table
      v-model="tableVisible"
      :tableDataSet="tableDataSet"
      v-if="tableVisible"
      @closeAddForm="closeAddForm"
      @confirm="confirm"
    ></custom-table>
  </div>
</template>

<script>
import vDialog from '@/views/operationAnalysis/common/dialog'
import outofstockRecords from './components/outofstockRecords/outofstockRecords'
import reply from './components/reply/reply'
import customProps from './customProps.mixin'
import pagePathMixin from '@/utils/page-path-mixin'

export default {
  name: 'OperationAnalysisPackages',
  components: { vDialog, outofstockRecords, reply },
  mixins: [customProps, pagePathMixin],
  props: ['data', 'timeText'],
  data () {
    return {
      orderParams: {},
      tableData: null,
      paginationOpt: {
        pageSizes: [30, 100, 500, 1000],
        pageNum: 1,
        pageSize: 30,
        total: 0
      },
      dialogVisibleRecord: false,
      dialogVisibleReply: false,
      rowData: null,
      exportForm: {},
      rowType: false,
      columnJurisdiction: false,
      scrollTop: 0,
      expectShipDailyMap: {},
      consumedDialogVisible: false,
      consumedDialogRow: {},
      consumedDialogData: [],
      consumedDialogLoading: false
    }
  },
  computed: {
    formData () {
      return this.data
    },
    maxHeight () {
      const winH = document.documentElement.clientHeight
      return `${winH - 404}px`
    }
  },
  activated () {
    this.getScroll()
  },
  mounted () {
    this.buttonJurisdiction()
    this.$refs.table.bodyWrapper.addEventListener('scroll', this.handleTableScroll, false)
    this.init()
  },
  deactivated () {
    this.$refs.table && this.$refs.table.bodyWrapper.removeEventListener('scroll', this.handleTableScroll, false)
  },
  methods: {
    handleTableScroll (event) {
      this.scrollTop = event.target.scrollTop
    },
    buttonJurisdiction () {
      this.$q.user.authListUserButton({ params: { subAppCode: 'ec-pc-new' } }).then(({ data }) => {
        const jurisdiction = 'ec-pc-new$orderManagement$analysisOrderManage$analysisOrder$Editimportnum'
        this.columnJurisdiction = data.includes(jurisdiction)
      })
    },
    updateAdd (data) {
      if (this.rowType && !data.row.flag) {
        this.$message.error('请在上一条编辑完成后再进行编辑')
        return
      }
      this.rowType = true
      this.rowData = JSON.parse(JSON.stringify(data.row))
      const rowSet = JSON.parse(JSON.stringify(data.row))
      rowSet.flag = !rowSet.flag
      this.$set(this.tableData, data.$index, rowSet)
      if (!rowSet.flag) {
        this.handleSelectChange(data.row)
      }
    },
    cancel (data) {
      this.$set(this.tableData, data.$index, this.rowData)
      this.rowType = false
    },
    input (e, scope) {
      if (scope.column.label.includes('下线')) {
        scope.row[scope.column.property] = scope.row[scope.column.property].replace(/[^\d]/g, '')
      }
    },
    handleSelectChange (data) {
      this.$q.orderDate.shippingInfoUpdate({ data }).then((res) => {
        if (res.success) {
          this.$message.success('修改成功')
        } else {
          this.$message.fail('修改失败')
        }
        this.rowType = false
      }).catch(() => {
        this.rowType = false
      }).finally(() => {
        this.init()
      })
    },
    getScroll () {
      setTimeout(() => {
        const tableBody = this.$el.querySelector('.el-table__body-wrapper')
        if (tableBody) tableBody.scrollTop = this.scrollTop
      }, 10)
    },
    search () {
      this.paginationOpt.pageNum = 1
      this.init()
    },
    init () {
      this.tableData = null
      const data = {
        filterData: {
          ...this.formData,
          ...this.orderParams
        },
        pageNum: this.paginationOpt.pageNum,
        pageSize: this.paginationOpt.pageSize
      }
      this.exportForm = data
      this.$q.orderDate.deliveryShortagePackListPage({ data }).then((res) => {
        if (res.success && res.data && res.data.records) {
          this.pagingColumnData = res.data.titleList
          this.customColumn()
          this.tableData = res.data.records.map((item, index) => ({ ...item, flag: false, index }))
          this.paginationOpt = {
            ...this.paginationOpt,
            total: res.data.total || 0,
            pageNum: res.data.pageNum || 1
          }
        } else {
          this.tableData = []
          this.paginationOpt = { ...this.paginationOpt, total: 0 }
        }
      })
    },
    handleTabsChange (row, tabName) {
      this.$emit('handleTabsChange', {
        formData: {
          packCode: row.packCode,
          outStockOrder: true
        },
        tabName
      })
    },
    handleDialogShow (type, row) {
      if (type === 'record') {
        this.dialogVisibleRecord = true
        this.rowData = {
          packCode: row.packCode,
          packName: row.packName,
          shortageDays: row.shortageDays
        }
      } else if (type === 'reply') {
        this.dialogVisibleReply = true
        this.rowData = row
        this.$router.replace({ path: this.$route.path, query: { reply: '' } })
      } else if (type === 'replyReadOnly') {
        this.dialogVisibleReply = true
        this.rowData = row
        this.$router.replace({ path: this.$route.path, query: { reply: 'readOnly' } })
      }
    },
    handleSortChange ({ prop, order }) {
      const orderType = { ascending: 'ASC', descending: 'DESC' }
      this.paginationOpt.pageSize = 30
      this.paginationOpt.pageNum = 1
      this.orderParams.sortField = prop
      this.orderParams.sortOptions = orderType[order]
      this.init()
    },
    handleSizeChange (val) {
      this.paginationOpt.pageSize = val
      this.paginationOpt.pageNum = 1
      this.init()
    },
    handleCurrentChange (val) {
      this.paginationOpt.pageNum = val
      this.init()
    },
    exportExcel () {
      const loading = this.$loading({
        lock: true,
        text: 'Loading',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      const params = {
        taskDTO: {
          appCode: 'ec-pc-new',
          paramMap: this.exportForm.filterData,
          reportName: 'shortagePackExport'
        },
        columnDTOs: []
      }
      this.columnData.forEach((item) => {
        params.columnDTOs.push({
          columnName: item.label,
          parentColumnName: null,
          fieldName: item.prop,
          fieldType: 'string',
          columnFormat: null
        })
      })
      params.columnDTOs = params.columnDTOs.filter(v => v.columnName !== '图片')
      this.$q.common.customExportExcel({ data: params }).then((res) => {
        if (res.success) {
          this.$message.success('提交下载任务成功，请稍后到导出列表查询')
        } else {
          this.$message.error(res.message)
        }
      }).finally(() => {
        loading.close()
      })
    },
    openProgressPage () {
      this.openSubPage({
        path: '/lackPackagesSummary'
      })
    },
    loadExpectShipDaily (row) {
      if (this.expectShipDailyMap[row.packCode]) return
      this.$q.orderDate.workflowPackExpectShipDaily({
        data: {
          packageCode: row.packCode
        }
      }).then((res) => {
        if (res.success && res.data) {
          this.$set(this.expectShipDailyMap, row.packCode, res.data)
        }
      })
    },
    getDailyWeeks (dailyData) {
      if (!dailyData || !Array.isArray(dailyData.weeks)) return []
      return dailyData.weeks.map((week) => ({
        weekLabel: week.weekLabel || '-',
        days: Array.isArray(week.days) ? week.days : []
      }))
    },
    formatConsumedDialogValue (value) {
      return value === null || value === undefined || value === '' ? '-' : value
    },
    getWarehouseGap (row) {
      const fields = [
        'warehouseGap',
        'inWarehouseGap',
        'warehouseShortageNum',
        'stockInShortageNum',
        'inboundShortageNum',
        'storageShortageNum'
      ]
      const field = fields.find(key => row[key] !== undefined && row[key] !== null && row[key] !== '')
      return field ? row[field] : '-'
    },
    formatConsumedDialogData (list) {
      if (!Array.isArray(list)) return []
      return list.map((item) => ({
        platformOrderNo: this.formatConsumedDialogValue(item.platformOrderNo),
        orderNo: this.formatConsumedDialogValue(item.orderNo),
        purchaseOrderNo: this.formatConsumedDialogValue(item.purchaseOrderNo),
        payTime: this.formatConsumedDialogValue(item.payTime),
        auditTime: this.formatConsumedDialogValue(item.auditTime),
        latestShippingTime: this.formatConsumedDialogValue(item.latestShippingTime),
        deliveryCycle: this.formatConsumedDialogValue(item.deliveryCycle),
        lastDays: this.formatConsumedDialogValue(item.lastDays),
        orderSource: this.formatConsumedDialogValue(item.orderSource),
        productCode: this.formatConsumedDialogValue(item.productCode),
        soTime: this.formatConsumedDialogValue(item.soTime)
      }))
    },
    openConsumedDialog (row) {
      this.consumedDialogRow = row
      this.consumedDialogVisible = true
      this.consumedDialogData = []
      this.consumedDialogLoading = true
      this.$q.orderDate.deliveryPackOrderList({
        params: {
          pack_code: row.packCode
        }
      }).then((res) => {
        this.consumedDialogData = res.success ? this.formatConsumedDialogData(res.data) : []
        if (!res.success) {
          this.$message.error(res.message || '查询已开单消耗量失败')
        }
      }).catch(() => {
        this.consumedDialogData = []
        this.$message.error('查询已开单消耗量失败')
      }).finally(() => {
        this.consumedDialogLoading = false
      })
    }
  }
}
</script>

<style lang="stylus" scoped>
.tabs-item
  height 100%

.toolbar-row
  display flex
  justify-content flex-end
  gap 8px
  margin-bottom 10px

.footer-row
  display flex
  justify-content space-between
  align-items center
  margin 10px 0

.refresh-time
  color #555

.daily-table
  min-width 600px

.daily-week-block
  padding 10px 0
  border-bottom 1px solid #ebeef5

.daily-week-block:last-child
  border-bottom 0

.daily-week-title
  font-weight 600
  color #606266
  margin-bottom 6px

.daily-row
  display flex
  gap 8px
  padding 6px 0
  align-items center
  text-align center

.daily-row span
  flex 1
  min-width 0

.daily-head
  font-weight 600

.daily-loading
  color #909399
  padding 10px 0

.consumed-header
  margin-bottom 12px
  color #606266
</style>
